import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../services/api';
import { RingLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { formatPrice } from '../utils/currency';

const MyOrdersPage = () => {
    const { t, i18n } = useTranslation();
    const { lang } = useParams();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchWithAuth('/orders/my');
            setOrders(data || []);
            setError(null);
        } catch (err) {
            console.error("Error fetching orders:", err);
            setError(err.message || t('myOrdersPage.errorFetching'));
            toast.error(t('myOrdersPage.errorFetchingToast'));
            setOrders([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    if (loading) {
        return <div className="flex justify-center items-center h-screen"><RingLoader color="#4A90E2" size={80} /></div>;
    }

    if (error) {
        return <div className="container mx-auto px-4 py-8 text-center"><p className="text-red-500 text-xl">{error}</p></div>;
    }

    return (
        <div className="container mx-auto px-4 py-12 bg-gray-50 min-h-screen" data-testid="my-orders-page">
            <h1 className="text-4xl font-bold text-center mb-10 text-gray-800" data-testid="my-orders-page-title">{t('myOrdersPage.title')}</h1>
            {orders.length === 0 ? (
                <div className="text-center text-gray-600" data-testid="my-orders-no-orders">
                    <p className="text-xl mb-4">{t('myOrdersPage.noOrdersFound')}</p>
                    <Link 
                        to={`/${lang}/products`}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-150 ease-in-out"
                        data-testid="my-orders-browse-products-link"
                    >
                        {t('myOrdersPage.browseProductsButton')}
                    </Link>
                </div>
            ) : (
                <div className="space-y-8" data-testid="my-orders-list">
                    {orders.map(order => (
                        <div key={order.id} className="bg-white shadow-xl rounded-lg p-6 md:p-8" data-testid={`my-order-${order.id}`}>
                            <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 pb-4 border-b border-gray-200">
                                <div>
                                    <h2 className="text-2xl font-semibold text-indigo-600" data-testid={`my-order-id-${order.id}`}>{t('myOrdersPage.orderIdLabel')} #{order.id}</h2>
                                    <p className="text-sm text-gray-500" data-testid={`my-order-date-${order.id}`}>{t('myOrdersPage.placedOnLabel')} {new Date(order.createdAt).toLocaleDateString()}</p>
                                </div>
                                <div className="mt-3 md:mt-0 text-right">
                                    <p className="text-lg font-medium text-gray-700" data-testid={`my-order-total-${order.id}`}>{t('myOrdersPage.totalAmountLabel')} <span className="text-xl font-bold text-gray-900">{formatPrice(order.totalAmount, lang)}</span></p>
                                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${ 
                                        order.status === 'DELIVERED' ? 'bg-green-100 text-green-700' : 
                                        order.status === 'SHIPPED' ? 'bg-purple-100 text-purple-700' :
                                        order.status === 'ORDER_ACCEPTED' ? 'bg-indigo-100 text-indigo-700' :
                                        order.status === 'ORDER_RECEIVED' ? 'bg-blue-100 text-blue-700' :
                                        order.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                                        order.status === 'REFUNDED' ? 'bg-orange-100 text-orange-700' :
                                        'bg-gray-100 text-gray-700' 
                                    }`} data-testid={`my-order-status-${order.id}`}>
                                        {t(`orderStatus.orderStatuses.${order.status}`, order.status)}
                                    </span>
                                </div>
                            </div>

                            {/* QR Code Section */}
                            {order.qrCode && (
                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM9 6a1 1 0 000 2v2a1 1 0 001 1h2a1 1 0 100-2V9a1 1 0 00-1-1H9zM6 13a1 1 0 100 2h2a1 1 0 100-2H6zM17 11a1 1 0 100-2h-2a1 1 0 100 2h2zM17 15a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM13 17a1 1 0 100-2v-2a1 1 0 10-2 0v2a1 1 0 001 1h1z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-blue-900">Плочка со QR Code:</p>
                                            <p className="text-lg font-mono font-semibold text-blue-700" data-testid={`my-order-qr-code-${order.id}`}>{order.qrCode}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Size Section */}
                            {order.productSize && (
                                <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-purple-900">{t('checkoutPage.sizeLabel')}:</p>
                                            <p className="text-lg font-semibold text-purple-700" data-testid={`my-order-size-${order.id}`}>
                                                {order.productSize === 'S' && 'S (6x6 cm)'}
                                                {order.productSize === 'M' && 'M (8x8 cm)'}
                                                {order.productSize === 'L' && 'L (10x10 cm)'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('myOrdersPage.itemsLabel')}</h3>
                                {order.orderItems.map(item => (
                                    <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0" data-testid={`my-order-item-${item.id}`}>
                                        <div className="flex items-center">
                                            <img 
                                                src={item.productImageUrl || '/placeholder-image.jpg'} 
                                                alt={item.productName} 
                                                className="w-16 h-16 object-cover rounded-md mr-4 shadow"
                                                onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.jpg'; }}
                                                data-testid={`my-order-item-image-${item.id}`}
                                            />
                                            <div>
                                                <p className="font-medium text-gray-800" data-testid={`my-order-item-name-${item.id}`}>{item.productName}</p>
                                                <p className="text-sm text-gray-500" data-testid={`my-order-item-quantity-${item.id}`}>{t('myOrdersPage.quantityLabel')}: {item.quantity}</p>
                                            </div>
                                        </div>
                                        <p className="text-md font-semibold text-gray-700" data-testid={`my-order-item-total-${item.id}`}>{formatPrice(item.lineTotal, lang)}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Order Status History */}
                            {order.statusHistory && order.statusHistory.length > 0 && (
                                <div className="mb-4 pt-4 border-t border-gray-200" data-testid={`my-order-status-history-${order.id}`}>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-3">{t('myOrdersPage.orderProgressLabel')}</h3>
                                    <div className="space-y-3">
                                        {order.statusHistory.map((history, index) => (
                                            <div key={history.id} className="flex items-start gap-3" data-testid={`my-order-history-item-${history.id}`}>
                                                <div className={`w-3 h-3 rounded-full mt-1.5 ${
                                                    history.status === 'DELIVERED' ? 'bg-green-500' : 
                                                    history.status === 'SHIPPED' ? 'bg-purple-500' :
                                                    history.status === 'ORDER_ACCEPTED' ? 'bg-indigo-500' :
                                                    history.status === 'ORDER_RECEIVED' ? 'bg-blue-500' :
                                                    history.status === 'CANCELLED' ? 'bg-red-500' :
                                                    history.status === 'REFUNDED' ? 'bg-orange-500' :
                                                    'bg-gray-500'
                                                }`}></div>
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <p className="font-medium text-gray-800" data-testid={`my-order-history-status-${history.id}`}>
                                                                {t(`orderStatus.orderStatuses.${history.status}`, history.status)}
                                                            </p>
                                                            {history.description && (
                                                                <p className="text-sm text-gray-600 mt-1" data-testid={`my-order-history-description-${history.id}`}>
                                                                    {history.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-gray-500" data-testid={`my-order-history-date-${history.id}`}>
                                                            {new Date(history.changedAt).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {order.shippingAddress && (
                                <div className="pt-4 border-t border-gray-200" data-testid={`my-order-shipping-address-${order.id}`}>
                                     <h3 className="text-lg font-semibold text-gray-700 mb-1">{t('myOrdersPage.shippingAddressLabel')}</h3>
                                     <p className="text-gray-600 text-sm whitespace-pre-line">{order.shippingAddress}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyOrdersPage; 